import { Box, TextField, Button, IconButton, Avatar, Typography, Divider } from "@mui/material";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createPost } from "../utils/api";
import { addPost } from "../features/postsSlice";

const CreatePost = () => {
  const authData = useSelector((state) => state.auth);
  const user = authData.user;
  const dispatch = useDispatch();

  const [content, setContent] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(null);
    setImagePreview(null);
  };

  const handlePostSubmit = async () => {
    if (content.trim() === "") {
      setError("Post content cannot be empty.");
      return;
    }
    setError(null);
    setLoading(true);

    const postData = new FormData();
    postData.append("content", content);
    postData.append("author", user._id);
    if (image) postData.append("image", image);

    try {
      const post = await createPost(postData);
      dispatch(addPost(post));
      setContent("");
      handleRemoveImage();
    } catch (err) {
      setError("Error uploading post. Please try again.");
      console.error("Error uploading post:", err);
    } finally {
      setLoading(false);
    }
  };

  const initials = `${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? ""}`.toUpperCase();

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "14px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
        mb: 2,
        overflow: "hidden",
        width: "100%",
      }}
    >
      {/* Composer row */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 2, pb: 1.5 }}>
        <Avatar
          src={user?.profilePicture}
          sx={{
            width: 40,
            height: 40,
            mt: 0.5,
            border: "2px solid #E5E7EB",
            bgcolor: "#D4AF37",
            color: "#1A1A2E",
            fontSize: 14,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>
        <TextField
          placeholder={`What's on your mind, ${user?.firstName}?`}
          multiline
          minRows={2}
          maxRows={8}
          fullWidth
          variant="outlined"
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            if (error) setError(null);
          }}
          error={Boolean(error)}
          helperText={error}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              bgcolor: "#F8F9FA",
              fontSize: 14,
              "&:hover fieldset": { borderColor: "#D4AF37" },
              "&.Mui-focused fieldset": { borderColor: "#1A1A2E", borderWidth: 1.5 },
            },
            "& fieldset": { borderColor: "transparent" },
          }}
        />
      </Box>

      {/* Image preview */}
      {imagePreview && (
        <Box sx={{ px: 2, pb: 1.5, position: "relative" }}>
          <Box sx={{ position: "relative", display: "inline-block", width: "100%" }}>
            <img
              src={imagePreview}
              alt="Post Preview"
              style={{
                width: "100%",
                maxHeight: 280,
                objectFit: "cover",
                borderRadius: "10px",
                display: "block",
              }}
            />
            <IconButton
              size="small"
              onClick={handleRemoveImage}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(0,0,0,0.55)",
                color: "#fff",
                width: 28,
                height: 28,
                "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      )}

      <Divider sx={{ mx: 2 }} />

      {/* Action bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <input
            accept="image/*"
            id="create-post-image-upload"
            type="file"
            style={{ display: "none" }}
            onChange={handleImageChange}
          />
          <label htmlFor="create-post-image-upload">
            <IconButton
              component="span"
              size="small"
              sx={{
                color: "#6B7280",
                "&:hover": { color: "#D4AF37", bgcolor: "rgba(212,175,55,0.1)" },
              }}
            >
              <AddAPhotoIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </label>
          <Typography sx={{ color: "#9CA3AF", fontSize: 12.5, ml: 0.5 }}>
            Add photo
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handlePostSubmit}
          disabled={loading || content.trim() === ""}
          endIcon={<SendRoundedIcon sx={{ fontSize: 16 }} />}
          size="small"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13.5,
            px: 2.5,
            py: 0.8,
            background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)",
            boxShadow: "none",
            "&:hover": {
              background: "linear-gradient(135deg, #D4AF37 0%, #b8941f 100%)",
              color: "#1A1A2E",
            },
            "&.Mui-disabled": {
              opacity: 0.5,
              background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)",
              color: "#fff",
            },
          }}
        >
          {loading ? "Posting…" : "Post"}
        </Button>
      </Box>
    </Box>
  );
};

export default CreatePost;
